import {ActionList, type ActionListItemProps} from '@primer/react'
import {ArrowUpRightIcon, FileIcon} from '@primer/octicons-react'
import {memo, type ComponentType, type ReactNode} from 'react'

import type {APITaskArtifact} from '../../../types/artifact'
import {isValidHttpUrl} from '../../../utils/is-valid-http-url'
import {ActionBarItem} from './ActionBarItem'

interface ResourceArtifactItemProps {
  artifact: APITaskArtifact
  repoNwo: string
  label?: never
  onSelect?: never
}

interface PresentedArtifactItemProps {
  artifact?: never
  repoNwo?: never
  label: string
  description?: ReactNode
  leadingVisual?: ComponentType
  trailingVisual?: ComponentType
  active?: boolean
  inactiveText?: string
  onSelect: NonNullable<ActionListItemProps['onSelect']>
  'data-artifact-path'?: string
}

export type ArtifactItemProps = ResourceArtifactItemProps | PresentedArtifactItemProps

export const ArtifactItem = memo(function ArtifactItemRow(props: ArtifactItemProps) {
  if (!props.artifact) {
    const {
      label,
      description,
      leadingVisual: LeadingVisual = FileIcon,
      trailingVisual,
      active,
      inactiveText,
      onSelect,
      'data-artifact-path': dataArtifactPath,
    } = props

    if (inactiveText) {
      return (
        <ActionList.Item active={active} inactiveText={inactiveText} data-artifact-path={dataArtifactPath}>
          <ActionList.LeadingVisual>
            <LeadingVisual />
          </ActionList.LeadingVisual>
          {label}
          {description !== undefined && description !== null && (
            <ActionList.Description>{description}</ActionList.Description>
          )}
        </ActionList.Item>
      )
    }

    return (
      <ActionBarItem
        active={active}
        description={description}
        leadingVisual={LeadingVisual}
        trailingVisual={trailingVisual}
        onClick={onSelect}
        data-artifact-path={dataArtifactPath}
      >
        {label}
      </ActionBarItem>
    )
  }

  const {artifact, repoNwo} = props
  const presentation = getArtifactPresentation(artifact, repoNwo)
  const href = presentation.href

  if (!href) {
    return (
      <ActionList.Item inactiveText="This artifact is unavailable">
        <ActionList.LeadingVisual>
          <FileIcon />
        </ActionList.LeadingVisual>
        {presentation.label}
      </ActionList.Item>
    )
  }

  return (
    <ActionBarItem
      onClick={() => {
        window.location.href = href
      }}
      leadingVisual={FileIcon}
      trailingVisual={ArrowUpRightIcon}
    >
      {presentation.label}
    </ActionBarItem>
  )
})

ArtifactItem.displayName = 'ArtifactItem'

function getArtifactPresentation(artifact: APITaskArtifact, repoNwo: string): {label: string; href?: string} {
  switch (artifact.type) {
    case 'branch':
      return {
        label: artifact.data.head_ref,
        href: `/${repoNwo}/tree/${encodeURIComponent(artifact.data.head_ref)}`,
      }
    case 'github_resource': {
      const resourcePath =
        artifact.data.type === 'pull_request' ? 'pull' : artifact.data.type === 'issue' ? 'issues' : null
      const resourceType = artifact.data.type.replaceAll('_', ' ')
      const label = `${resourceType.charAt(0).toUpperCase()}${resourceType.slice(1)} #${artifact.data.id}`

      return {
        label,
        ...(resourcePath ? {href: `/${repoNwo}/${resourcePath}/${artifact.data.id}`} : {}),
      }
    }
    case 'azure_devops_resource':
      return {
        label: 'Azure DevOps resource',
        href: isValidHttpUrl(artifact.data.url) ? artifact.data.url : undefined,
      }
  }
}
